/**
 * Template: SidebarLayout — Production B2B chrome
 * Lucide SVG icons, gold active nav, top bar with search + notifications + user avatar
 * Branding-aware: uses BrandingContext for logo + company name in sidebar
 * i18n-aware: uses react-i18next for navigation labels
 * Mobile: bottom nav bar + hamburger slide-over for full menu
 */
import React, { useState, useEffect, useCallback, useRef } from "react"
import { apiGet } from "@/lib/api"
import { isIOS } from "@/lib/native"
import { MortiseMark, MortiseWordmark } from "@/components/atoms/MortiseMark"
import { IconButton } from "@/components/atoms/IconButton"
import { NotificationPanel, Notification } from "@/components/organisms/NotificationPanel"
import { notificationRouteFor } from "@/lib/notifications"
import { useSSEEvent } from "@/hooks/useSSE"
import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { tokens } from "@/design-system/tokens"
import { useBreakpoint } from "@/design-system/breakpoints"
import { useBranding } from "@/contexts/BrandingContext"
import { useProject } from "@/contexts/ProjectContext"
import { useTranslation } from "react-i18next"
import { useTheme } from "@/context/ThemeContext"
import { usePermissions, usePermissionsBootstrap } from "@/lib/usePermissions"
import { useProjectPins } from "@/hooks/useProjectPins"
import { ChatFABPanel } from '@/components/organisms/ChatFABPanel'
import { WhatsNewModal } from '@/components/organisms/WhatsNewModal'
import { OnboardingTour } from '@/components/organisms/OnboardingTour'
import { HelpPanel } from '@/components/organisms/HelpPanel'
import { BillingBanner } from '@/components/molecules/BillingBanner'
import { DemoBanner } from '@/components/molecules/DemoBanner'
import { PreviewBanner } from '@/components/molecules/PreviewBanner'
import { authedFetch, getToken } from '@/lib/api';
import { ReferencePopover } from '@/components/molecules/ReferencePopover'
import { TimerPanel } from '@/components/molecules/TimerPanel'
import {
  Layout, LayoutDashboard, FolderKanban, CalendarDays, ClipboardList, ClipboardCheck, ChevronDown, Globe, Layers, Mail,
  DollarSign, FileText, Receipt, FileDiff, ShoppingCart, Handshake,
  User, Users, Target, Clock, CheckSquare, HelpCircle, MessageSquare, MessageCircle,
  Bot, Settings, FileCheck, Gavel, Bell, Search, LogOut, ChevronLeft, ChevronRight, Menu, X, BarChart2, TrendingUp,
  Shield, ShieldCheck, Calendar, Truck, Warehouse, Activity, Building2, Sparkles, Wallet, FolderOpen, Grid3X3, Briefcase, LayoutTemplate, BookOpen, AlertTriangle, PhoneCall, Home, Eye, EyeOff, Palette, Pin, Inbox
} from "lucide-react"

// Bottom nav items for mobile — Today-first, 4 core tabs + More drawer
const BOTTOM_NAV_MOBILE = [
  { path: "/m/today", icon: Home, label: "Today" },
  { path: "/m/time", icon: Clock, label: "Time" },
  { path: "sage", icon: Bot, label: "Sage" }, // special: opens chat
  { path: "/messages", icon: MessageCircle, label: "Messages" },
]
const BOTTOM_NAV = BOTTOM_NAV_MOBILE

const ROUTE_LABELS: Record<string, string> = {
  dashboards: "Dashboard", projects: "Projects", leads: "Leads",
  takeoffs: "Takeoffs", estimates: "Estimates", proposals: "Proposals",
  decisions: "Decisions", procurement: "Procurement", permits: "Permits",
  tasks: "Schedule", "task-schedule": "Schedule", "change-orders": "Change Orders",
  rfis: "RFIs", "project-logs": "Project Logs", timesheets: "Timesheets",
  budget: "Budget", invoices: "Invoices", overhead: "Overhead",
  callbacks: "Callbacks", "home-service": "Service", maintenance: "Maintenance",
  goals: "Goals", equipment: "Equipment", locations: "Locations",
  consumables: "Consumables", safety: "Safety", compliance: "Compliance",
  contacts: "Contacts", settings: "Settings", profile: "Profile",
  changelog: "What's New", messages: "Messages", reports: "Reports",
  internal: "Internal", profitability: "Profitability", estimate: "Estimates",
  proposal: "Proposals", "cost-catalogue": "Cost Catalogue",
}

function BreadcrumbTrail({ pathname, activeProject, brandColor }: {
  pathname: string
  activeProject: { id: number; name: string } | null
  brandColor: string
}) {
  const segs = pathname.replace(/^\//, "").split("/").filter(Boolean)
  const crumbs: string[] = []

  if (segs[0] === "projects" && segs[1] && !/^\d+$/.test(segs[1])) {
    crumbs.push("Projects")
  } else if (segs[0] === "projects" && segs[1] && /^\d+$/.test(segs[1])) {
    if (activeProject) crumbs.push(activeProject.name)
    else crumbs.push("Project")
    const section = segs[2]
    if (section && ROUTE_LABELS[section]) crumbs.push(ROUTE_LABELS[section])
  } else if (segs[0] === "internal" && segs[1]) {
    crumbs.push("Internal")
    if (ROUTE_LABELS[segs[1]]) crumbs.push(ROUTE_LABELS[segs[1]])
  } else if (segs[0] && ROUTE_LABELS[segs[0]]) {
    crumbs.push(ROUTE_LABELS[segs[0]])
    if (segs[1] && ROUTE_LABELS[segs[1]]) crumbs.push(ROUTE_LABELS[segs[1]])
  }

  if (crumbs.length === 0) return null

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ opacity: 0.4 }}>/</span>}
          <span style={{
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            color: i === crumbs.length - 1 ? "var(--text-primary)" : "var(--text-muted)",
            fontWeight: i === crumbs.length - 1 ? 600 : 500,
          }}>{c}</span>
        </React.Fragment>
      ))}
    </div>
  )
}

interface SidebarLayoutProps {
  onOpenSearch?: () => void
  children: React.ReactNode
  currentPath?: string
  userName?: string
  userRole?: string
}

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  children,
  userName = "Tenon",
  userRole = "admin",
  onOpenSearch,
}) => {
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length-1][0]).toUpperCase()
    return (name.slice(0, 2).toUpperCase() || 'T')
  }
  // Humanize a role slug like "production_lead" → "Production Lead".
  const humanizeRole = (role: string) => {
    if (!role) return ''
    return role.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }
  const { t, i18n } = useTranslation()
  const { theme } = useTheme()
  const { isMobile, isTablet, isDesktop } = useBreakpoint()
  const navigate = useNavigate()
  // Detect Electron so we can clear the macOS traffic-light buttons (y:18, ~22px tall).
  const isElectron = typeof window !== "undefined" && "electronAPI" in (window as any)
  const routerLocation = useLocation()
  const { activeProject, setActiveProject, viewAll, setViewAll } = useProject()
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1')
  // Collapsible sidebar sections — persisted per section in localStorage. Default open.
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>(() => {
    const keys = ['preconstruction', 'construction', 'finance', 'internal', 'project-precon', 'project-construction', 'project-finance']
    const out: Record<string, boolean> = {}
    keys.forEach(k => {
      const v = localStorage.getItem(`sidebar_section_${k}`)
      out[k] = v === null ? true : v === '1'
    })
    return out
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchVal, setSearchVal] = useState("")
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [apiOnline, setApiOnline] = useState(true)
  const [isSageOpen, setIsSageOpen] = useState(false)
  const [sageUnread, setSageUnread] = useState(0)
  const [msgUnread, setMsgUnread] = useState(0)
  const [callbackBadge, setCallbackBadge] = useState(0)
  const [homeServiceBadge, setHomeServiceBadge] = useState({ overdue: 0, upcoming: 0 })
  const [goalsOffTrackBadge, setGoalsOffTrackBadge] = useState(0)
  const [sageInboxBadge, setSageInboxBadge] = useState(0)
  const [userAvatarUrl, setUserAvatarUrl] = useState('')
  const [userContactId, setUserContactId] = useState<number | null>(null)
  const [userParentContactId, setUserParentContactId] = useState<number | null>(null)
  // Live role/name from /api/v1/profile (DB-backed) — overrides the props
  // sourced from the JWT, which can drift when the user's contact role
  // changes mid-session and they haven't re-logged in.
  const [liveRole, setLiveRole] = useState<string | null>(null)
  const [liveName, setLiveName] = useState<string | null>(null)
  const [showTour, setShowTour] = useState(false)
  const [tourChecked, setTourChecked] = useState(false)
  const [tourUser, setTourUser] = useState<{name: string; role: string; companyName: string} | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  const [timerActive, setTimerActive] = useState(false)
  const helpRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!helpOpen) return
    const handler = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setHelpOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [helpOpen])
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const { can } = usePermissions()
  usePermissionsBootstrap()

  useEffect(() => {
    const check = () => {
      apiGet('/timesheets/active')
        .then((r: unknown) => setTimerActive(!!(r as { active?: boolean })?.active))
        .catch(() => {})
    }
    check()
    const id = setInterval(check, 30_000)
    return () => clearInterval(id)
  }, [])

  // Fix 1: hide bottom nav when soft keyboard is visible
  const [hideNav, setHideNav] = useState(false)
  useEffect(() => {
    const navHandler = (e: CustomEvent) => setHideNav(e.detail.open)
    window.addEventListener('sage-keyboard', navHandler as EventListener)
    return () => window.removeEventListener('sage-keyboard', navHandler as EventListener)
  }, [])

  const fetchNotifications = useCallback(async () => {
    try {
      const token = getToken()
      if (!token) return
      const res = await authedFetch("/api/v1/notifications", {}, { redirectOnAuthFailure: false })
      if (!res.ok) return
      const data = await res.json()
      setNotifications(data.items || [])
      setUnreadCount(data.unread_count || 0)
    } catch {}
  }, [])

  // SSE: live notification push — updates badge + prepends notification to list
  useSSEEvent('notification', useCallback((event) => {
    const p = event.payload as { id: number; title: string; body: string; entity_type?: string; entity_id?: number }
    if (!p?.id) return
    const newNotif: Notification = {
      id: p.id,
      user_id: 0,
      event_type: p.entity_type || 'notification',
      title: p.title || '',
      body: p.body || '',
      entity_type: p.entity_type || '',
      entity_id: p.entity_id ?? null,
      read_at: null,
      created_at: new Date().toISOString(),
    }
    setNotifications(prev => [newNotif, ...prev])
    setUnreadCount(prev => prev + 1)
  }, []))

  // Fallback polling at 5-min interval (catches any SSE misses on reconnect)
  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 300000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // Fetch messages unread count (aggregated: legacy + chat channels)
  const fetchMsgUnread = async () => {
    const token = getToken()
    if (!token) return
    let legacyUnread = 0
    let chatUnread = 0
    try {
      const res = await authedFetch('/api/v1/messages', {}, { redirectOnAuthFailure: false })
      if (res.ok) {
        const data = await res.json()
        const msgs: Array<{ read_at: string | null }> = data.data ?? []
        legacyUnread = msgs.filter(m => !m.read_at).length
      }
    } catch {}
    try {
      const chatRes = await authedFetch('/api/v1/chat/channels', {}, { redirectOnAuthFailure: false })
      if (chatRes.ok) {
        const channels = await chatRes.json()
        if (Array.isArray(channels)) {
          chatUnread = channels.reduce((sum: number, ch: { unread_count?: number }) => sum + (ch.unread_count || 0), 0)
        }
      }
    } catch {}
    setMsgUnread(legacyUnread + chatUnread)
  }

  useEffect(() => {
    fetchMsgUnread()
    const interval = setInterval(fetchMsgUnread, 60000)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sage "Needs you" inbox badge — separate from the general notifications
  // bell per the #2666 Design contract (never mixed into `unreadCount`).
  useEffect(() => {
    const fetchSageInbox = () => {
      if (!getToken()) return
      apiGet('/sage/inbox')
        .then((r: { counts?: { confirm?: number; flags?: number; completeness?: number; questions?: number } }) => {
          const c = r?.counts
          setSageInboxBadge((c?.confirm ?? 0) + (c?.flags ?? 0) + (c?.completeness ?? 0) + (c?.questions ?? 0))
        })
        .catch(() => {})
    }
    fetchSageInbox()
    const interval = setInterval(fetchSageInbox, 60000)
    return () => clearInterval(interval)
  }, [])

  // Fetch badge counts for Callbacks and Home Service (project-scoped when a project is active).
  // Type labels are tenant-configurable — we fetch task_types and match any
  // label that contains "callback" or "closeout" (case-insensitive) so the
  // combined label "Callback / Closeout" matches alongside any legacy pair.
  // Hardcoding "Callback"/"Closeout" strings used to drop the badge to 0 for
  // tenants who'd renamed the label.
  useEffect(() => {
    const fetchBadges = async () => {
      // Skip the badge fetches entirely when logged out; authedFetch attaches
      // the bearer token itself for the calls below.
      if (!getToken()) return
      const pid = activeProject?.id
      const scope = pid ? `&project_id=${pid}` : `&exclude_internal=true`
      try {
        // Load the tenant's task_types once per fetch and filter to
        // callback/closeout/maintenance. Falls back to the legacy strings
        // so the badges still work before the list is populated. Matches
        // useRefList('task_types') — same /settings/fields/{key} endpoint
        // shape: { values | items: (string | { label, value, is_active })[] }.
        const typesRes = await authedFetch(`/api/v1/settings/fields/task_types`, {}, { redirectOnAuthFailure: false })
          .then(r => r.ok ? r.json() : null).catch(() => null)
        const rawVals: unknown[] = Array.isArray(typesRes) ? typesRes
          : Array.isArray(typesRes?.values) ? typesRes.values
          : Array.isArray(typesRes?.items) ? typesRes.items
          : []
        const allTypes: string[] = rawVals
          .filter((v): v is { is_active?: boolean; label?: string; value?: string } | string =>
            typeof v === "string" || (typeof v === "object" && v !== null && (v as { is_active?: boolean }).is_active !== false))
          .map(v => typeof v === "string" ? v : (v as { label?: string; value?: string }).label ?? (v as { label?: string; value?: string }).value ?? "")
          .filter(Boolean)
        const cbTypes = allTypes.filter(t => /callback|closeout/i.test(t))
        const cbTypeCSV = (cbTypes.length ? cbTypes : ["Callback", "Closeout", "Callback / Closeout"]).join(",")
        // Service badge must mirror HomeServicePage's filter
        // (typeFilter: ["Service"]) so the count and the drilldown list
        // agree. Prior code fetched /maintenance/i — that's the separate
        // /maintenance nav entry, not /home-service. Use the exact
        // "Service" type to match the page one-for-one.
        const [cbRes, svcRes] = await Promise.all([
          authedFetch(`/api/v1/tasks?type=${encodeURIComponent(cbTypeCSV)}&limit=200${scope}`, {}, { redirectOnAuthFailure: false }).then(r => r.ok ? r.json() : { data: [] }),
          authedFetch(`/api/v1/tasks?type=${encodeURIComponent("Service")}&limit=200${scope}`, {}, { redirectOnAuthFailure: false }).then(r => r.ok ? r.json() : { data: [] }),
        ])
        const cbTasks = cbRes.data || []
        const openCb = cbTasks.filter((t: { status: string }) => t.status !== "Completed" && t.status !== "Cancelled")
        setCallbackBadge(openCb.length)
        const now = new Date()
        const in7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
        const svcTasks: Array<{ status: string; due_date?: string }> = svcRes.data || []
        const openSvc = svcTasks.filter(t => t.status !== "Completed" && t.status !== "Cancelled")
        const overdue = openSvc.filter(t => t.due_date && new Date(t.due_date) < now).length
        const upcoming = openSvc.filter(t => t.due_date && new Date(t.due_date) >= now && new Date(t.due_date) < in7).length
        setHomeServiceBadge({ overdue, upcoming })
        // Off-track rocks badge
        const offTrackRes = await authedFetch(`/api/v1/goals/my-off-track-count`, {}, { redirectOnAuthFailure: false })
          .then(r => r.ok ? r.json() : { count: 0 }).catch(() => ({ count: 0 }))
        setGoalsOffTrackBadge(offTrackRes.count || 0)
      } catch {}
    }
    fetchBadges()
    const interval = setInterval(fetchBadges, 300000)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id])

  // Fetch live profile data: avatar, role, and contact link.
  // Prefer the contact's photo + role over the user-table values when
  // present (e.g. when an admin sets a contact's role to a custom company
  // role like "Production Lead", we want that to show in the sidebar
  // immediately without waiting for re-login).
  useEffect(() => {
    const token = getToken()
    if (!token) return
    authedFetch('/api/v1/profile', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return
        const avatar = data.display_avatar_url || data.avatar_url
        if (avatar) setUserAvatarUrl(avatar)
        if (data.contact_id) setUserContactId(data.contact_id)
        if (data.parent_contact_id) setUserParentContactId(data.parent_contact_id)
        const role = data.display_role || data.role
        if (role) setLiveRole(role)
        if (data.name) setLiveName(data.name)
      })
      .catch(() => {})
  }, [])

    // Connection health check every 30s
  useEffect(() => {
    const checkHealth = async () => {
      try {
        await authedFetch('/api/v1/health', { signal: AbortSignal.timeout(3000) })
        setApiOnline(true)
      } catch {
        setApiOnline(false)
      }
    }
    checkHealth()
    const interval = setInterval(checkHealth, 30000)
    return () => clearInterval(interval)
  }, [])


  // Check on mount whether to show the onboarding tour
  useEffect(() => {
    if (tourChecked) return
    setTourChecked(true)
    const token = getToken()
    if (!token) return
    authedFetch("/api/v1/auth/me", {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return
        // Tour check is secondary — only show if not yet seen this session
        if (!data.tour_completed && !data.tour_dismissed_at && !sessionStorage.getItem("tour_shown")) {
          sessionStorage.setItem("tour_shown", "1")
          setTourUser({ name: data.name || '', role: data.role || '', companyName: data.company_name || '' })
          setTimeout(() => setShowTour(true), 600)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTourComplete = () => {
    setShowTour(false)
    const token = getToken()
    if (token) {
      authedFetch("/api/v1/auth/tour", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed: true }),
      }, { redirectOnAuthFailure: false }).catch(() => {})
    }
  }

  const handleTourDismiss = () => {
    setShowTour(false)
    const token = getToken()
    if (token) {
      authedFetch("/api/v1/auth/tour", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dismissed: true }),
      }, { redirectOnAuthFailure: false }).catch(() => {})
    }
  }

  const handleMarkRead = useCallback(async (id: number) => {
    await authedFetch(`/api/v1/notifications/${id}/read`, {
      method: "PATCH",
    }, { redirectOnAuthFailure: false })
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const handleMarkAllRead = useCallback(async () => {
    await authedFetch("/api/v1/notifications/read-all", {
      method: "POST",
    }, { redirectOnAuthFailure: false })
    setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })))
    setUnreadCount(0)
  }, [])

  // BUG-NOTIFICATIONS-POPOVER-ITEMS-INERT — single click on a notification
  // should: navigate to the source entity, mark read (if unread), close popover.
  const handleNotificationActivate = useCallback((notif: Notification) => {
    const route = notificationRouteFor(notif)
    if (!notif.read_at) {
      handleMarkRead(notif.id)
    }
    setNotifOpen(false)
    navigate(route)
  }, [handleMarkRead, navigate])
  const { branding } = useBranding()
  // Reset logo error when logo_url changes (user uploads a new logo)
  useEffect(() => { setLogoError(false) }, [branding.logo_url])
  const [projectList, setProjectList] = useState<Array<{id: number; name: string; status?: string}>>([])
  const [projectSelectorOpen, setProjectSelectorOpen] = useState(false)
  const [hideTerminalProjects, setHideTerminalProjects] = useState<boolean>(() => {
    try { return localStorage.getItem("ui.sidebar.projectSelector.hideTerminal") !== "0" } catch { return true }
  })
  useEffect(() => {
    try { localStorage.setItem("ui.sidebar.projectSelector.hideTerminal", hideTerminalProjects ? "1" : "0") } catch { /* ignore */ }
  }, [hideTerminalProjects])
  const { isPinned, togglePin } = useProjectPins()
  const [hoveredProjectId, setHoveredProjectId] = useState<number | null>(null)
  // Pinning implies always-visible — a pinned terminal project is never hidden by the eye toggle.
  const visibleProjectList = hideTerminalProjects
    ? projectList.filter(p => isPinned(p.id) || (p.status !== "completed" && p.status !== "cancelled"))
    : projectList
  const hiddenProjectCount = projectList.length - visibleProjectList.length
  // Stable sort: pinned projects float to a section above the rest, original order preserved within each group.
  const sortedVisibleProjectList = [...visibleProjectList].sort((a, b) => Number(isPinned(b.id)) - Number(isPinned(a.id)))
  const pinnedVisibleCount = sortedVisibleProjectList.filter(p => isPinned(p.id)).length

  // Load projects for selector
  useEffect(() => {
    if (!getToken()) return
    authedFetch("/api/v1/projects?limit=50", {}, { redirectOnAuthFailure: false })
      .then(r => r.json()).then(d => setProjectList(d.data || []))
      .catch(() => {})
  }, [])

  // Close project selector on outside click
  useEffect(() => {
    if (!projectSelectorOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-project-selector]')) setProjectSelectorOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [projectSelectorOpen])

  function projectHref(globalPath: string, localPath?: string): string {
    if (viewAll || !activeProject) return globalPath
    const seg = localPath || globalPath.slice(1)
    return `/projects/${activeProject.id}/${seg}`
  }

  // ── Stay-on-page helper ──
  // When switching projects, keep the user on the same section (e.g. estimates,
  // schedule, budget) rather than navigating to the project overview.
  const stayOnPagePath = (newProjectId: number | null) => {
    const path = routerLocation.pathname
    // Extract current section from project-scoped path: /projects/:id/:section/...
    const projectMatch = path.match(/^\/projects\/\d+\/(.+)/)
    // Or from global path: /:section/...
    const globalMatch = path.match(/^\/([a-z-]+)/)
    const section = projectMatch?.[1]?.split('/')[0] || globalMatch?.[1] || null
    // Sections that exist in both project-scoped and global nav
    const validSections = new Set([
      'profitability', 'decisions', 'takeoffs', 'estimate', 'proposal',
      'task-schedule', 'procurement', 'permits', 'issues', 'change-orders',
      'budget', 'submittals', 'invoices', 'project-logs', 'rfis',
      'timesheets', 'team', 'files', 'safety', 'compliance',
      'memory', // SPEC-PROJECT-MEMORY (#91)
    ])
    // Global-only sections (no project-scoped equivalent)
    const globalOnlySections = new Set([
      'dashboard', 'leads', 'contacts', 'cost-catalogue', 'settings',
      'profile', 'internal', 'projects',
    ])
    // Global-to-project mapping for sections with different names
    const globalToProject: Record<string, string> = {
      'tasks': 'task-schedule', 'estimates': 'estimate', 'proposals': 'proposal',
      'constraints': 'issues',
    }
    const projectToGlobal: Record<string, string> = {
      'task-schedule': 'tasks', 'estimate': 'estimates', 'proposal': 'proposals',
      'issues': 'constraints',
    }
    if (!section) {
      return newProjectId ? `/projects/${newProjectId}` : '/projects'
    }
    if (newProjectId) {
      // Switching to a specific project
      const projectSection = globalToProject[section] || (validSections.has(section) ? section : null)
      return projectSection ? `/projects/${newProjectId}/${projectSection}` : `/projects/${newProjectId}`
    } else {
      // Switching to All Projects
      if (globalOnlySections.has(section)) return `/${section}`
      const globalSection = projectToGlobal[section] || (section === 'budget' ? 'budget/overview' : section)
      return `/${globalSection}`
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("ml_token")
    navigate("/login")
  }

  const changeLanguage = async (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem("ml_lang", lang)
    // Persist to user profile (best-effort)
    const token = getToken()
    if (token) {
      try {
        await authedFetch("/api/v1/profile/language", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language: lang }),
        }, { redirectOnAuthFailure: false })
      } catch {}
    }
  }

  const brandColor = branding.primary_color || tokens.color.brand.gold
  // Per project_mobile_design.md §B-1 LOCKED 2026-05-05: sidebar is fixed
  // (always full-width, not user-collapsible) on tablet. The previous
  // behaviour forced isTablet to 60px (icon-only), which made the locked
  // "fixed on tablet vs collapsible on phone" contrast invisible. The
  // user-toggle path still works on desktop.
  const SIDEBAR_W = collapsed ? 60 : 224

  const NavItems = ({ onClose }: { onClose?: () => void }) => {
    const isProjectMode = !viewAll && !!activeProject
    const pid = activeProject?.id

    const navLinkStyle = (isActive: boolean): React.CSSProperties => ({
      display: "flex", alignItems: "center", gap: "8px",
      padding: collapsed ? "8px 0" : "6px 12px",
      margin: collapsed ? "2px 0" : "2px 8px",
      borderRadius: "6px",
      background: isActive ? "var(--bg-surface-hi)" : "transparent",
      borderLeft: collapsed ? undefined : (isActive ? `2px solid ${brandColor}` : "2px solid transparent"),
      color: isActive ? brandColor : tokens.color.text.secondary,
      textDecoration: "none", fontSize: "13px",
      fontWeight: isActive ? 600 : 400,
      fontFamily: tokens.typography.fontFamily.sans,
      justifyContent: collapsed ? "center" : "flex-start",
      transition: "background 120ms ease, color 120ms ease, border-color 120ms ease",
      minHeight: 36,
    })

    const renderLink = (to: string, label: string, Icon: React.ElementType, endMatch = false) => (
      <NavLink
        key={to + label}
        to={to}
        end={endMatch}
        onClick={() => { setMobileOpen(false); onClose?.() }}
        style={({ isActive }) => navLinkStyle(isActive)}
      >
        <Icon size={15} style={{ flexShrink: 0 }} />
        {!collapsed && (
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {label}
          </span>
        )}
      </NavLink>
    )

    const renderDivider = (key: string) => (
      <div key={key} style={{ height: 1, background: tokens.color.border.default, margin: "4px 8px" }} />
    )

    const renderGroupLabel = (label: string, key: string) => !collapsed ? (
      <div key={key} style={{
        padding: "8px 12px 4px", fontSize: "9.5px", fontWeight: 600,
        color: tokens.color.text.muted, textTransform: "uppercase" as const, letterSpacing: "0.12em",
        fontFamily: tokens.typography.fontFamily.mono,
      }}>{label}</div>
    ) : null

    const renderSection = (label: string, storageKey: string, children: React.ReactNode) => {
      if (collapsed) {
        return <React.Fragment key={`sec-${storageKey}`}>{children}</React.Fragment>
      }
      const lsKey = `sidebar_section_${storageKey}`
      const isOpen = sectionOpen[storageKey] ?? true
      return (
        <div key={`sec-${storageKey}`}>
          <button
            onClick={() => {
              const next = !isOpen
              setSectionOpen(prev => ({ ...prev, [storageKey]: next }))
              localStorage.setItem(lsKey, next ? '1' : '0')
            }}
            style={{
              width: "calc(100% - 16px)",
              margin: "4px 8px 0",
              padding: "6px 4px 4px 4px",
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "9.5px",
              fontWeight: 700,
              color: tokens.color.text.muted,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              fontFamily: tokens.typography.fontFamily.mono,
            }}
          >
            <ChevronDown
              size={12}
              style={{
                transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 150ms ease",
                flexShrink: 0,
              }}
            />
            <span>{label}</span>
          </button>
          {isOpen && <div>{children}</div>}
        </div>
      )
    }

    const renderLinkWithBadges = (to: string, label: string, Icon: React.ElementType, badges: Array<{ count: number; color: string; bg: string }> = []) => (
      <NavLink
        key={to + label}
        to={to}
        onClick={() => { setMobileOpen(false); onClose?.() }}
        style={({ isActive }) => navLinkStyle(isActive)}
      >
        <Icon size={15} style={{ flexShrink: 0 }} />
        {!collapsed && (
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {label}
          </span>
        )}
        {!collapsed && badges.filter(b => b.count > 0).map((b, i) => (
          <span key={i} style={{
            minWidth: 18, height: 18, borderRadius: 99,
            background: b.bg, color: b.color,
            fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", flexShrink: 0,
          }}>{b.count > 99 ? "99+" : b.count}</span>
        ))}
      </NavLink>
    )

    if (isProjectMode && pid) {
      // ── MODE 2: Single Project nav ──
      // Per-section visibility: a section header + container render only when at
      // least one item under it is visible to the current user. "Memory" has no
      // backend resource so it's always visible → the Construction section is
      // never empty and needs no section-level gate.
      const showProjPrecon = can('takeoffs','view') || can('estimates','view') || can('proposals','view')
        || can('purchase_orders','view') || can('permits','view')
      return (
        <>
          {renderLink(`/projects/${pid}`, "Project Overview", Building2, true)}
          {showProjPrecon && renderDivider("d1")}
          {showProjPrecon && renderSection("Preconstruction", "project-precon", (
            <>
              {can('takeoffs','view') && renderLink(`/projects/${pid}/takeoffs`, "Takeoffs", Layers)}
              {can('estimates','view') && renderLink(`/projects/${pid}/estimate`, "Estimates", FileText)}
              {can('proposals','view') && renderLink(`/projects/${pid}/proposal`, "Proposals", ClipboardList)}
              {can('purchase_orders','view') && renderLink(`/projects/${pid}/procurement`, "Procurement", ShoppingCart)}
              {can('permits','view') && renderLink(`/projects/${pid}/permits`, "Permits", ClipboardCheck)}
            </>
          ))}
          {renderDivider("d2")}
          {renderSection("Construction", "project-construction", (
            <>
              {can('schedule','view') && renderLink(`/projects/${pid}/task-schedule`, "Schedule", CalendarDays)}
              {(can('finish_schedule','view') || can('decisions','view')) && renderLink(`/projects/${pid}/finish-schedule`, "Finish Schedule", Palette)}
              {can('change_orders','view') && renderLink(`/projects/${pid}/change-orders`, "Change Orders", FileDiff)}
              {can('rfis','view') && renderLink(`/projects/${pid}/rfis`, "RFIs", HelpCircle)}
              {can('daily_logs','view') && renderLink(`/projects/${pid}/project-logs`, "Project Logs", ClipboardList)}
              {can('timesheets','view') && renderLink(`/projects/${pid}/timesheets`, "Timesheets", Clock)}
              {can('team','view') && renderLink(`/projects/${pid}/team`, "Project Team", Users)}
              {renderLink(`/projects/${pid}/memory`, "Memory", BookOpen)}
            </>
          ))}
          {renderDivider("d3")}
          {(can('budget','view') || can('bills','view') || can('client_invoices','view')) && renderSection("Finance", "project-finance", (
            <>
              {can('budget','view') && renderLink(`/projects/${pid}/profitability`, "Profitability", TrendingUp)}
              {can('budget','view') && renderLink(`/projects/${pid}/budget`, "Budget", DollarSign)}
              {can('bills','view') && renderLink(`/projects/${pid}/procurement?tab=bills`, "Bills & Expenses", Wallet)}
              {can('client_invoices','view') && renderLink(`/projects/${pid}/invoices`, "Invoices", Receipt)}
              {renderLinkWithBadges(`/projects/${pid}/callbacks`, "Callbacks", PhoneCall, [{ count: callbackBadge, color: tokens.color.feedback.error, bg: `color-mix(in oklch, ${tokens.color.feedback.error} 13%, transparent)` }])}
              {renderLinkWithBadges(`/projects/${pid}/home-service`, "Service", Home, [
                { count: homeServiceBadge.overdue, color: tokens.color.feedback.error, bg: `color-mix(in oklch, ${tokens.color.feedback.error} 13%, transparent)` },
                { count: homeServiceBadge.upcoming, color: tokens.color.text.muted, bg: `color-mix(in oklch, ${tokens.color.text.muted} 20%, transparent)` },
              ])}
              {renderLink(`/projects/${pid}/safety`, "Safety", Shield)}
            </>
          ))}
        </>
      )
    }

    // ── MODE 1: All Projects nav ──
    // Per-section visibility: a section header + container render only when at
    // least one item under it is visible to the current user. The Internal
    // section keeps several ungated items (Tasks, Safety, Compliance)
    // so it's never empty.
    const showPrecon = can('leads','view') || can('takeoffs','view') || can('estimates','view')
      || can('proposals','view') || can('finish_schedule','view') || can('decisions','view')
      || can('purchase_orders','view') || can('permits','view')
    const showConstruction = can('schedule','view') || can('change_orders','view') || can('rfis','view')
      || can('daily_logs','view') || can('timesheets','view')
    return (
      <>
        {renderLink("/dashboards", "Dashboard", LayoutDashboard)}
        {renderLink("/projects", "Projects", FolderKanban, true)}
        {showPrecon && renderDivider("d1")}
        {showPrecon && renderSection("Preconstruction", "preconstruction", (
          <>
            {can('leads','view') && renderLink("/leads", "Leads", Target)}
            {can('takeoffs','view') && renderLink("/takeoffs", "Takeoffs", Layers)}
            {can('estimates','view') && renderLink("/estimates", "Estimates", FileText)}
            {can('proposals','view') && renderLink("/proposals", "Proposals", ClipboardList)}
            {(can('finish_schedule','view') || can('decisions','view')) && renderLink("/finish-schedule", "Finish Schedule", Palette)}
            {can('purchase_orders','view') && renderLink("/procurement", "Procurement", ShoppingCart)}
            {can('permits','view') && renderLink("/permits", "Permits", ClipboardCheck)}
          </>
        ))}
        {showConstruction && renderDivider("d2")}
        {showConstruction && renderSection("Construction", "construction", (
          <>
            {can('schedule','view') && renderLink("/tasks", "Schedule", CalendarDays)}
            {can('change_orders','view') && renderLink("/change-orders", "Change Orders", FileDiff)}
            {can('rfis','view') && renderLink("/rfis", "RFIs", HelpCircle)}
            {can('daily_logs','view') && renderLink("/project-logs", "Project Logs", ClipboardList)}
            {can('timesheets','view') && renderLink("/timesheets", "Timesheets", Clock)}
          </>
        ))}
        {(can('budget','view') || can('bills','view') || can('client_invoices','view')) && renderDivider("d3")}
        {(can('budget','view') || can('bills','view') || can('client_invoices','view')) && renderSection("Finance", "finance", (
          <>
            {can('budget','view') && renderLink("/budget/overview", "Budget Overview", BarChart2)}
            {can('reports','view') && renderLink("/reports", "Reports", TrendingUp)}
            {can('bills','view') && renderLink("/procurement?tab=bills", "Bills & Expenses", Wallet)}
            {can('client_invoices','view') && renderLink("/invoices", "Invoices", Receipt)}
            {(['admin','manager'].includes(liveRole||userRole)) && renderLink("/overhead", "Overhead", Briefcase)}
            {/* App Store 3.1.1 — hide the Internal Billing link on iOS so
                the sidebar never surfaces a "billing" entry point on the
                native build. Subscription management is web-only. */}
            {!isIOS() && (['admin','manager'].includes(liveRole||userRole)) && renderLink("/internal/billing", "Internal Billing", Receipt)}
            {renderLinkWithBadges("/callbacks", "Callbacks", PhoneCall, [{ count: callbackBadge, color: tokens.color.feedback.error, bg: `color-mix(in oklch, ${tokens.color.feedback.error} 13%, transparent)` }])}
            {renderLinkWithBadges("/home-service", "Service", Home, [
              { count: homeServiceBadge.overdue, color: tokens.color.feedback.error, bg: `color-mix(in oklch, ${tokens.color.feedback.error} 13%, transparent)` },
              { count: homeServiceBadge.upcoming, color: tokens.color.text.muted, bg: `color-mix(in oklch, ${tokens.color.text.muted} 20%, transparent)` },
            ])}
            {renderLink("/maintenance", "Maintenance", Activity)}
          </>
        ))}
        {renderDivider("d4")}
        {renderSection("Internal", "internal", (
          <>
            {renderLinkWithBadges("/goals", "Goals", Target, [{ count: goalsOffTrackBadge, color: tokens.color.feedback.error, bg: `color-mix(in oklch, ${tokens.color.feedback.error} 13%, transparent)` }])}
            {renderLink("/internal/tasks", t("internal.nav.tasks", "Tasks"), CheckSquare)}
            {renderLink("/safety", "Safety", Shield)}
            {renderLink("/compliance", "Compliance", ShieldCheck)}
          </>
        ))}
      </>
    )
  }

  return (
    // `--chat-dock-w` is published on :root by ChatFABPanel while the chat
    // panel is DOCKED (desktop only; 0/unset otherwise). Subtracting it here is
    // what makes the app RESIZE BESIDE the panel instead of sitting underneath
    // it — this shell is height:100vh/overflow:hidden, so it ignores body-level
    // padding (the previous approach was a silent no-op). Fixed-position
    // descendants that span the full width must subtract the same var.
    <div style={{
      display: "flex", height: "100vh", overflow: "hidden", background: "var(--bg-page)",
      width: "calc(100% - var(--chat-dock-w, 0px))",
      transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
    }}>
      {/* ── MOBILE LAYOUT ── */}
      {isMobile ? (
        <>
          {/* Mobile slide-over (full menu) */}
          {mobileOpen && (
            <div
              onClick={() => setMobileOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 998 }}
            />
          )}
          <aside
            style={{
              position: "fixed",
              top: 0, left: 0, bottom: 0,
              width: 280,
              background: "var(--bg-surface)",
              borderRight: "1px solid var(--border)",
              zIndex: 999,
              display: "flex", flexDirection: "column",
              transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: mobileOpen ? "4px 0 24px rgba(0,0,0,0.4)" : "none",
            }}
          >
            {/* Logo */}
            <div style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "14px 12px", borderBottom: `1px solid ${tokens.color.border.default}`,
              justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MortiseWordmark size={18} />
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: tokens.color.text.muted, padding: "4px" }}
              >
                <X size={18} />
              </button>
            </div>
            {/* Nav */}
            <nav style={{ flex: 1, padding: "8px 0", overflowY: "auto" }}>
              <NavItems onClose={() => setMobileOpen(false)} />
            </nav>
            {/* User footer */}
            <div style={{
              borderTop: `1px solid ${tokens.color.border.default}`,
              padding: "12px",
              display: "flex", alignItems: "center", gap: "10px",
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: userAvatarUrl ? 'transparent' : `${brandColor}33`, border: `1px solid ${brandColor}66`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "13px", fontWeight: 700, color: brandColor,
                overflow: 'hidden', flexShrink: 0,
              }}>
                {userAvatarUrl ? (
                  <img src={userAvatarUrl} alt={liveName || userName} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                ) : getInitials(liveName || userName)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: tokens.color.text.primary }}>{liveName || userName}</div>
                <div style={{ fontSize: "11px", color: tokens.color.text.muted }}>{humanizeRole(liveRole || userRole)}</div>
              </div>
              <button onClick={handleLogout} title="Sign out"
                style={{ background: "none", border: "none", color: tokens.color.text.muted, cursor: "pointer", padding: "4px" }}>
                <LogOut size={16} />
              </button>
            </div>
          </aside>

          {/* Mobile main area */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {/* Mobile top bar */}
            <header style={{
              height: "calc(52px + env(safe-area-inset-top))", borderBottom: `1px solid ${tokens.color.border.default}`,
              display: "flex", alignItems: "center", padding: "env(safe-area-inset-top) 16px 0 16px", gap: "12px",
              background: "var(--bg-surface)", flexShrink: 0, position: "sticky", top: 0, zIndex: 100,
            }}>
              <button
                onClick={() => setMobileOpen(true)}
                style={{ background: "none", border: "none", cursor: "pointer", color: tokens.color.text.muted, padding: "4px", display: "flex" }}
              >
                <Menu size={20} color={tokens.color.text.primary} />
              </button>
              {/* Brand name */}
              <span style={{ fontWeight: 700, fontSize: "14px", color: tokens.color.text.primary, flex: 1 }}>
                {branding.company_name || "Tenon"}
              </span>
              {/* Sage "Needs you" inbox — separate badge from Notifications below */}
              <button
                onClick={() => navigate("/sage/inbox")}
                aria-label="Sage — Needs you"
                style={{ background: "none", border: "none", color: tokens.color.text.muted, cursor: "pointer", padding: "4px", display: "flex", alignItems: "center", position: "relative" }}
              >
                <Inbox size={20} />
                {sageInboxBadge > 0 && (
                  <span style={{
                    position: "absolute", top: 0, right: 0,
                    minWidth: 14, height: 14, borderRadius: "9999px",
                    background: tokens.color.brand.gold, color: tokens.color.text.inverse,
                    fontSize: "9px", fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
                  }}>{sageInboxBadge > 99 ? "99+" : sageInboxBadge}</span>
                )}
              </button>
              {/* Notifications */}
              <button
                onClick={() => setNotifOpen(o => !o)}
                style={{ background: "none", border: "none", color: tokens.color.text.muted, cursor: "pointer", padding: "4px", display: "flex", alignItems: "center", position: "relative" }}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: 0, right: 0,
                    minWidth: 14, height: 14, borderRadius: "9999px",
                    background: brandColor, color: "#fff",
                    fontSize: "9px", fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
                  }}>{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
              </button>
              <NotificationPanel
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkRead={handleMarkRead}
                onMarkAllRead={handleMarkAllRead}
                onActivate={handleNotificationActivate}
              />
            </header>

            {/* Page content — padded for bottom nav */}
            <PreviewBanner />
            <DemoBanner />
            <BillingBanner />
            <main style={{ flex: 1, overflow: "auto", padding: "16px 16px calc(72px + env(safe-area-inset-bottom)) 16px", minWidth: 0 }}>
              {!apiOnline && (
                <div style={{ background: 'var(--st-error)', color: 'var(--bg-page)', padding: '6px 12px', fontSize: 12, textAlign: 'center', borderRadius: 6, marginBottom: 12, fontFamily: 'inherit' }}>
                  Connection lost — retrying...
                </div>
              )}
              {children}
            </main>
          </div>

          {/* Bottom navigation bar */}
          <nav style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            height: "calc(56px + env(safe-area-inset-bottom))",
            paddingBottom: "env(safe-area-inset-bottom)",
            background: "var(--bg-surface)",
            borderTop: "1px solid var(--border)",
            display: hideNav ? "none" : "flex", alignItems: "center", justifyContent: "space-around",
            zIndex: 100,
          }}>
            {BOTTOM_NAV.map(item => {
              // Special case: Sage chat button
              if (item.path === "sage") {
                return (
                  <button
                    key="sage"
                    onClick={() => setIsSageOpen(o => !o)}
                    style={{
                      flex: 1, display: "flex", flexDirection: "column" as const,
                      alignItems: "center", gap: 3,
                      padding: "8px 4px", background: "none", border: "none", cursor: "pointer",
                      color: isSageOpen ? brandColor : tokens.color.text.muted,
                    }}
                  >
                    <MessageSquare size={22} strokeWidth={isSageOpen ? 2.5 : 2} color={isSageOpen ? brandColor : tokens.color.text.muted} />
                    <span style={{
                      fontSize: "10px", fontWeight: isSageOpen ? 600 : 400,
                      fontFamily: tokens.typography.fontFamily.sans,
                      color: isSageOpen ? brandColor : tokens.color.text.muted,
                    }}>Sage</span>
                    {sageUnread > 0 && (
                      <span style={{
                        position: "absolute" as const, top: 6,
                        minWidth: 14, height: 14, borderRadius: "9999px",
                        background: brandColor, color: "#fff",
                        fontSize: "9px", fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
                      }}>{sageUnread}</span>
                    )}
                  </button>
                )
              }
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/" && item.href === "/"}
                  style={({ isActive }) => ({
                    flex: 1, display: "flex", flexDirection: "column" as const,
                    alignItems: "center", gap: 3,
                    padding: "8px 4px", textDecoration: "none",
                    color: isActive ? brandColor : tokens.color.text.muted,
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                      <span style={{
                        fontSize: "10px", fontWeight: isActive ? 600 : 400,
                        fontFamily: tokens.typography.fontFamily.sans,
                      }}>{item.label}</span>
                      {item.path === "/notifications" && unreadCount > 0 && (
                        <span style={{
                          position: "absolute" as const, top: 6,
                          minWidth: 14, height: 14, borderRadius: "9999px",
                          background: brandColor, color: "#fff",
                          fontSize: "9px", fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
                        }}>{unreadCount}</span>
                      )}
                      {item.path === "/m/time" && timerActive && (
                        <>
                          <style>{`@keyframes timer-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.7)}}`}</style>
                          <span style={{
                            position: "absolute" as const, top: 7, right: "calc(50% - 14px)",
                            width: 7, height: 7, borderRadius: "50%",
                            background: brandColor,
                            animation: "timer-pulse 1.6s ease-in-out infinite",
                          }} />
                        </>
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
            {/* More button — opens full-screen slide-up drawer */}
            <button
              onClick={() => setMoreDrawerOpen(o => !o)}
              style={{
                flex: 1, display: "flex", flexDirection: "column" as const,
                alignItems: "center", gap: 3,
                padding: "8px 4px", background: "none", border: "none", cursor: "pointer",
                color: moreDrawerOpen ? brandColor : tokens.color.text.muted,
              }}
            >
              <Grid3X3 size={22} strokeWidth={moreDrawerOpen ? 2.5 : 2} color={moreDrawerOpen ? brandColor : tokens.color.text.muted} />
              <span style={{ fontSize: "10px", fontWeight: moreDrawerOpen ? 600 : 400, fontFamily: tokens.typography.fontFamily.sans }}>More</span>
            </button>
          </nav>

          {/* ── MORE DRAWER (slide-up overlay) ── */}
          {moreDrawerOpen && (
            <div
              onClick={() => setMoreDrawerOpen(false)}
              style={{
                position: "fixed", inset: 0,
                background: "rgba(0,0,0,0.55)",
                zIndex: 200,
              }}
            />
          )}
          <div style={{
            position: "fixed",
            bottom: 0, left: 0, right: 0,
            background: "var(--bg-surface)",
            borderTop: "1px solid var(--border)",
            borderRadius: "22px 22px 0 0",
            zIndex: 201,
            transform: moreDrawerOpen ? "translateY(0)" : "translateY(100%)",
            transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
            maxHeight: "80vh",
            overflowY: "auto",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}>
            {/* Drag handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px', flexShrink: 0 }}>
              <div style={{ width: 36, height: 5, borderRadius: 3, background: 'var(--border-strong)' }} />
            </div>
            {/* Drawer header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px 12px",
              borderBottom: `1px solid ${tokens.color.border.default}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: tokens.color.text.primary, fontFamily: tokens.typography.fontFamily.sans }}>More</span>
              <button onClick={() => setMoreDrawerOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: tokens.color.text.muted, padding: 4, display: "flex" }}>
                <X size={20} />
              </button>
            </div>

            {/* Financial section */}
            <div style={{ padding: "12px 20px 4px" }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: tokens.color.text.muted, marginBottom: 4, fontFamily: tokens.typography.fontFamily.mono }}>
                Financial
              </div>
              {[
                { path: "/invoices", label: "Invoices", perm: ['client_invoices','view'] as [string,string] },
                { path: "/estimates", label: "Estimates", perm: ['estimates','view'] as [string,string] },
                { path: "/change-orders", label: "Change Orders", perm: ['change_orders','view'] as [string,string] },
                { path: "/procurement", label: "Procurement", perm: ['purchase_orders','view'] as [string,string] },
                { path: "/reports", label: "Reports", perm: ['reports','view'] as [string,string] },
              ].filter(item => can(item.perm[0], item.perm[1])).map(item => (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMoreDrawerOpen(false) }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "14px 0", background: "none", border: "none",
                    borderBottom: `1px solid ${tokens.color.border.default}`,
                    fontSize: 15, color: tokens.color.text.primary,
                    cursor: "pointer", fontFamily: tokens.typography.fontFamily.sans,
                    minHeight: 48,
                  }}
                >{item.label}</button>
              ))}
            </div>

            {/* Project section */}
            <div style={{ padding: "12px 20px 4px" }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: tokens.color.text.muted, marginBottom: 4, fontFamily: tokens.typography.fontFamily.mono }}>
                Project
              </div>
              {[
                { path: "/rfis", label: "RFIs", perm: ['rfis','view'] as [string,string] },
                { path: "/submittals", label: "Submittals", perm: ['submittals','view'] as [string,string] },
                { path: "/files", label: "Files", perm: ['files','view'] as [string,string] },
                { path: "/tasks", label: "Schedule", perm: null },
                { path: "/timesheets", label: "Timesheets", perm: ['timesheets','view'] as [string,string] },
                { path: "/equipment", label: "Equipment", perm: null },
                { path: "/locations", label: "Locations", perm: null },
              ].filter(item => !item.perm || can(item.perm[0], item.perm[1])).map(item => (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMoreDrawerOpen(false) }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "14px 0", background: "none", border: "none",
                    borderBottom: `1px solid ${tokens.color.border.default}`,
                    fontSize: 15, color: tokens.color.text.primary,
                    cursor: "pointer", fontFamily: tokens.typography.fontFamily.sans,
                    minHeight: 48,
                  }}
                >{item.label}</button>
              ))}
            </div>

            {/* Settings section */}
            {(can('company_settings','view') || ['admin','super_admin'].includes((liveRole || userRole || ''))) && (
              <div style={{ padding: "12px 20px 20px" }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: tokens.color.text.muted, marginBottom: 4, fontFamily: tokens.typography.fontFamily.mono }}>
                  Settings
                </div>
                {[
                  { path: "/settings", label: "Settings" },
                ].map(item => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMoreDrawerOpen(false) }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "14px 0", background: "none", border: "none",
                      borderBottom: `1px solid ${tokens.color.border.default}`,
                      fontSize: 15, color: tokens.color.text.primary,
                      cursor: "pointer", fontFamily: tokens.typography.fontFamily.sans,
                      minHeight: 48,
                    }}
                  >{item.label}</button>
                ))}
              </div>
            )}
          </div>

        </>
      ) : (
        /* ── DESKTOP LAYOUT ── */
        <>
          {/* Mobile overlay (for tablet collapsed nav) */}
          {mobileOpen && (
            <div
              onClick={() => setMobileOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 998 }}
            />
          )}

          {/* Sidebar */}
          <aside
            style={{
              width: SIDEBAR_W,
              flexShrink: 0,
              background: "var(--bg-surface)",
              borderRight: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              transition: "width 0.15s, transform 0.2s",
              position: mobileOpen ? "fixed" : "relative",
              height: "100vh",
              overflowY: "auto",
              zIndex: mobileOpen ? 999 : 1,
            }}
            className="sidebar sidebar-scroll"
          >
            {/* Logo / brand — data-drag-region lets Electron drag the window from here.
                 Height pinned to same 86px as header so both borderBottoms align. */}
            <div
              data-drag-region
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                height: isElectron ? 86 : 52,
                boxSizing: "border-box" as const,
                padding: collapsed
                  ? `${isElectron ? 44 : 13}px 0 13px`
                  : `${isElectron ? 44 : 13}px 12px 13px`,
                borderBottom: `1px solid ${tokens.color.border.default}`,
                justifyContent: collapsed ? "center" : "space-between",
                flexShrink: 0,
              }}>
              {!collapsed && (
                <NavLink to="/projects" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                  <MortiseWordmark size={17} />
                </NavLink>
              )}
              {collapsed && (
                <NavLink to="/projects" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                  <MortiseMark size={26} />
                </NavLink>
              )}
              {!collapsed && !isTablet && (
                <button
                  onClick={() => { const next = !collapsed; setCollapsed(next); localStorage.setItem('sidebar_collapsed', next ? '1' : '0'); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: tokens.color.text.muted, padding: "2px", display: "flex" }}
                >
                  <ChevronLeft size={14} />
                </button>
              )}
            </div>

            {/* Project Selector */}
            {!collapsed && (
              <div data-project-selector style={{ margin: "6px 8px 0", position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setProjectSelectorOpen(o => !o)}
                  style={{
                    padding: "7px 10px",
                    borderRadius: "7px",
                    border: `1px solid ${activeProject ? brandColor + "88" : tokens.color.border.default}`,
                    background: activeProject ? `${brandColor}11` : tokens.color.background.elevated,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    width: "100%",
                    textAlign: "left",
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                >
                  <Layers size={12} color={activeProject ? brandColor : tokens.color.text.muted} style={{ flexShrink: 0 }} />
                  <span style={{
                    flex: 1, fontSize: "11px", fontWeight: 600,
                    color: activeProject ? brandColor : tokens.color.text.muted,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {activeProject ? activeProject.name : "All Projects"}
                  </span>
                  <ChevronDown size={11} color={tokens.color.text.muted} />
                </button>
                {projectSelectorOpen && (
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: "absolute", left: 0, top: "100%", marginTop: "4px",
                      width: "210px", background: tokens.color.background.surface,
                      border: `1px solid ${tokens.color.border.default}`, borderRadius: "8px",
                      boxShadow: "0 8px 28px rgba(0,0,0,0.35)", zIndex: 1000, overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => { const target = stayOnPagePath(null); setActiveProject(null); setViewAll(false); setProjectSelectorOpen(false); navigate(target) }}
                      style={{
                        padding: "5px 12px", fontSize: "12px", cursor: "pointer",
                        color: !activeProject ? brandColor : tokens.color.text.secondary,
                        fontWeight: !activeProject ? 600 : 400,
                        display: "flex", alignItems: "center", gap: "6px",
                        width: "100%", background: "transparent", border: "none",
                        borderBottom: `1px solid ${tokens.color.border.default}`,
                        textAlign: "left", fontFamily: "inherit", outline: "none",
                      }}
                    >
                      <Globe size={12} /> All Projects
                    </button>
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    {pinnedVisibleCount > 0 && (
                      <div style={{
                        padding: "6px 12px 2px", fontSize: 9.5, fontWeight: 700,
                        letterSpacing: "0.12em", textTransform: "uppercase" as const,
                        color: tokens.color.text.muted, fontFamily: tokens.typography.fontFamily.mono,
                      }}>
                        Pinned
                      </div>
                    )}
                    {sortedVisibleProjectList.map((p, idx) => {
                      const pinned = isPinned(p.id)
                      const selectRow = () => {
                        const target = stayOnPagePath(p.id)
                        setActiveProject({ id: p.id, name: p.name, status: p.status })
                        setViewAll(false)
                        setProjectSelectorOpen(false)
                        navigate(target)
                      }
                      return (
                        <React.Fragment key={p.id}>
                          {idx === pinnedVisibleCount && pinnedVisibleCount > 0 && (
                            <div style={{ borderTop: `1px solid ${tokens.color.border.default}` }} />
                          )}
                          <div
                            role="button"
                            tabIndex={0}
                            onMouseEnter={() => setHoveredProjectId(p.id)}
                            onMouseLeave={() => setHoveredProjectId(null)}
                            onClick={selectRow}
                            onKeyDown={e => {
                              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectRow() }
                            }}
                            style={{
                              padding: "5px 12px", fontSize: "12px", cursor: "pointer",
                              color: String(activeProject?.id) === String(p.id) ? brandColor : tokens.color.text.secondary,
                              fontWeight: String(activeProject?.id) === String(p.id) ? 600 : 400,
                              width: "100%", background: "transparent", border: "none",
                              borderBottom: `1px solid ${tokens.color.border.default}`,
                              fontFamily: "inherit", outline: "none",
                              display: "flex", alignItems: "center", gap: 6,
                            }}
                          >
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p.name}
                            </span>
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); togglePin(p.id) }}
                              title={pinned ? "Unpin project" : "Pin project"}
                              style={{
                                flexShrink: 0, background: "transparent", border: "none", cursor: "pointer",
                                padding: 2, display: "flex", alignItems: "center",
                                opacity: pinned || hoveredProjectId === p.id ? 1 : 0,
                                pointerEvents: pinned || hoveredProjectId === p.id ? "auto" : "none",
                                transition: "opacity 0.12s",
                              }}
                            >
                              <Pin size={11} color={pinned ? brandColor : tokens.color.text.muted} style={pinned ? { fill: brandColor } : {}} />
                            </button>
                          </div>
                        </React.Fragment>
                      )
                    })}
                    </div>
                    {/* Compact hide-completed toggle — eye icon + hidden count. */}
                    {(hiddenProjectCount > 0 || !hideTerminalProjects) && (
                      <button
                        type="button"
                        onClick={() => setHideTerminalProjects(v => !v)}
                        title={hideTerminalProjects
                          ? `Show ${hiddenProjectCount} completed / cancelled project${hiddenProjectCount === 1 ? "" : "s"}`
                          : "Hide completed & cancelled"}
                        style={{
                          width: "100%",
                          padding: "5px 12px",
                          fontSize: 10,
                          cursor: "pointer",
                          color: tokens.color.text.muted,
                          borderTop: `1px solid ${tokens.color.border.default}`,
                          background: "transparent",
                          border: "none",
                          display: "flex", alignItems: "center", gap: 6,
                          fontFamily: tokens.typography.fontFamily.sans,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = tokens.color.text.secondary)}
                        onMouseLeave={e => (e.currentTarget.style.color = tokens.color.text.muted)}
                      >
                        {hideTerminalProjects
                          ? <EyeOff size={11} style={{ flexShrink: 0 }} />
                          : <Eye size={11} style={{ flexShrink: 0 }} />}
                        <span>
                          {hideTerminalProjects
                            ? (hiddenProjectCount > 0 ? `${hiddenProjectCount} hidden` : "")
                            : "Hide completed"}
                        </span>
                      </button>
                    )}
                    {activeProject && (
                      <button
                        type="button"
                        onClick={() => { setViewAll(!viewAll); setProjectSelectorOpen(false) }}
                        style={{
                          padding: "7px 12px", fontSize: "11px", cursor: "pointer",
                          color: viewAll ? brandColor : tokens.color.text.muted,
                          display: "flex", alignItems: "center", gap: "4px",
                          width: "100%", background: "transparent", border: "none",
                          textAlign: "left", fontFamily: "inherit", outline: "none",
                        }}
                      >
                        ⊞ {viewAll ? "Back to project view" : "View all (global nav)"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Nav */}
            <nav className="sidebar-scroll" style={{ flex: 1, padding: "8px 0", overflowY: "auto", overflowX: "hidden" }}>
              {collapsed && !isTablet && (
                <button
                  onClick={() => { const next = !collapsed; setCollapsed(next); localStorage.setItem('sidebar_collapsed', next ? '1' : '0'); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: "100%", padding: "8px 0", background: "none", border: "none",
                    cursor: "pointer", color: tokens.color.text.muted,
                    borderBottom: "1px solid var(--border)",
                    marginBottom: "4px",
                  }}
                >
                  <ChevronRight size={14} />
                </button>
              )}
              <NavItems />
            </nav>


            {/* What's New — below nav, above user profile */}
            <NavLink
              to="/changelog"
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: "8px",
                padding: collapsed ? "8px 0" : "6px 12px",
                margin: collapsed ? "2px 0" : "2px 8px",
                borderRadius: "6px",
                background: isActive ? `${brandColor}22` : "transparent",
                color: isActive ? brandColor : tokens.color.text.secondary,
                textDecoration: "none", fontSize: "13px",
                fontWeight: isActive ? 600 : 400,
                fontFamily: tokens.typography.fontFamily.sans,
                justifyContent: collapsed ? "center" : "flex-start",
                transition: "background 120ms ease, color 120ms ease",
                minHeight: 36,
                flexShrink: 0,
              })}
            >
              <Sparkles size={15} style={{ flexShrink: 0 }} />
              {!collapsed && (
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  What's New
                </span>
              )}
            </NavLink>

            {/* User footer */}
            <div onClick={() => navigate(userParentContactId ? `/contacts/${userParentContactId}/profile` : userContactId ? `/contacts/${userContactId}/profile` : "/profile")} style={{
              borderTop: `1px solid ${tokens.color.border.default}`,
              padding: collapsed ? "12px 0" : "12px 12px",
              display: "flex", alignItems: "center", gap: "10px",
              justifyContent: collapsed ? "center" : "flex-start",
              flexShrink: 0, cursor: "pointer",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: userAvatarUrl ? 'transparent' : `${brandColor}33`, border: `1px solid ${brandColor}66`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", fontWeight: 700, color: brandColor, flexShrink: 0,
                overflow: 'hidden',
              }}>
                {userAvatarUrl ? (
                  <img src={userAvatarUrl} alt={liveName || userName} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                ) : getInitials(liveName || userName)}
              </div>
              {!collapsed && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: tokens.color.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{liveName || userName}</div>
                  <div style={{ fontSize: "11px", color: tokens.color.text.muted }}>{humanizeRole(liveRole || userRole)}</div>
                </div>
              )}
              {!collapsed && (
                <button onClick={handleLogout} title="Sign out"
                  style={{ background: "none", border: "none", color: tokens.color.text.muted, cursor: "pointer", padding: "2px", display: "flex" }}>
                  <LogOut size={13} />
                </button>
              )}
            </div>
          </aside>

          {/* Main area */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
            {/* Top bar — in Electron the sidebar logo area has 44px top clearance for
               traffic lights. MortiseWordmark size={17} renders at 17×1.6=27.2px tall,
               so sidebar logo height = 44 + 28 + 14 = 86px. Header must match exactly. */}
            <header
              data-drag-region
              style={{
                height: isElectron ? 86 : 52,
                borderBottom: `1px solid ${tokens.color.border.default}`,
                display: "flex",
                alignItems: "center",
                padding: "0 24px",
                gap: "12px",
                background: "var(--bg-page)",
                flexShrink: 0,
              }}>
              {/* Search — left-anchored where page title used to be */}
              <div style={{ position: "relative" }}>
                <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: tokens.color.text.muted }} />
                <input
                  value={searchVal}
                  onClick={onOpenSearch} onFocus={e => { e.target.blur(); onOpenSearch?.(); }} readOnly
                  placeholder="Search… (⌘K)"
                  style={{
                    width: 260,
                    background: tokens.color.background.elevated,
                    border: `1px solid ${tokens.color.border.default}`,
                    borderRadius: "6px",
                    padding: "6px 10px 6px 30px",
                    fontSize: "13px",
                    color: tokens.color.text.primary,
                    outline: "none",
                    fontFamily: tokens.typography.fontFamily.sans,
                    boxSizing: "border-box", cursor: "pointer",
                  }}
                />
              </div>

              <div style={{ flex: 1 }} />
              {/* Timer — clock in/out from any page rather than navigating to
                  Timesheets first (Joe, 2026-09-15), same shape as the desktop
                  app's menubar. Hidden on mobile, which has its own Time tab. */}
              {!isMobile && <TimerPanel mode="nav" />}
              {/* Sage "Needs you" inbox — separate badge from the general
                  notifications bell below (#2666 Design contract: never mixed). */}
              <IconButton
                aria-label="Sage — Needs you"
                title="Sage — Needs you"
                onClick={() => navigate("/sage/inbox")}
                size="md"
                style={{ position: "relative" }}
              >
                <Inbox size={16} />
                {sageInboxBadge > 0 && (
                  <span style={{
                    position: "absolute", top: 2, right: 2,
                    minWidth: 14, height: 14, borderRadius: "9999px",
                    background: tokens.color.brand.gold, color: tokens.color.text.inverse,
                    fontSize: "9px", fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 3px",
                  }}>{sageInboxBadge > 99 ? "99+" : sageInboxBadge}</span>
                )}
              </IconButton>
              {/* Notifications — Phase D §07.5 icon-button geometry parity */}
              <IconButton
                aria-label="Notifications"
                onClick={() => setNotifOpen(o => !o)}
                active={notifOpen}
                size="md"
                style={{ position: "relative" }}
              >
                <Bell size={16} />
                {unreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: 2, right: 2,
                    minWidth: 14, height: 14, borderRadius: "9999px",
                    background: brandColor, color: "#fff",
                    fontSize: "9px", fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 3px",
                  }}>{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
              </IconButton>
              <NotificationPanel
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkRead={handleMarkRead}
                onMarkAllRead={handleMarkAllRead}
                onActivate={handleNotificationActivate}
              />
              {/* Settings — Phase D §07.5 icon-button geometry parity */}
              <IconButton
                aria-label="Settings"
                onClick={() => navigate("/settings")}
                size="md"
              >
                <Settings size={16} />
              </IconButton>
              {/* Reference popover — replaces Sage in desktop top nav */}
              <ReferencePopover brandColor={brandColor} />
              {/* Help / Tour button — Phase D §07.5 icon-button geometry parity */}
              <div ref={helpRef} style={{ position: "relative" }}>
                <IconButton
                  aria-label="Help"
                  onClick={() => setHelpOpen(o => !o)}
                  active={helpOpen}
                  size="md"
                  title="Help"
                >
                  <HelpCircle size={16} />
                </IconButton>
                {helpOpen && (
                  <div style={{
                    position: "absolute", right: 0, top: 34,
                    width: 192, background: "var(--bg-surface)",
                    border: "1px solid var(--border)", borderRadius: 8,
                    boxShadow: "0 8px 28px rgba(0,0,0,0.4)",
                    zIndex: 200, overflow: "hidden",
                    fontFamily: tokens.typography.fontFamily.sans,
                  }}>
                    <button
                      onClick={() => { setHelpOpen(false); setShowTour(true) }}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "8px 16px", fontSize: 13,
                        color: "var(--text-primary)", background: "none",
                        border: "none", cursor: "pointer",
                        fontFamily: "inherit", display: "block",
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-elevated)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "none")}
                    >
                      Take the tour again
                    </button>
                    <button
                      onClick={() => { setHelpOpen(false); setHelpPanelOpen(true) }}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "8px 16px", fontSize: 13,
                        color: "var(--text-primary)", background: "none",
                        border: "none", cursor: "pointer",
                        fontFamily: "inherit", display: "block",
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-elevated)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "none")}
                    >
                      Help Centre
                    </button>
                    <button
                      onClick={() => { setHelpOpen(false); setHelpPanelOpen(true) }}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "8px 16px", fontSize: 13,
                        color: "var(--text-primary)", background: "none",
                        border: "none", cursor: "pointer",
                        fontFamily: "inherit", display: "block",
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-elevated)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "none")}
                    >
                      Contact Support
                    </button>
                  </div>
                )}
              </div>
              {/* Company branding — top right */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {branding.logo_url && !logoError ? (
                  <img
                    src={branding.logo_url}
                    alt="Logo"
                    onError={() => setLogoError(true)}
                    style={{ height: 24, objectFit: "contain", borderRadius: 3 }}
                  />
                ) : branding.company_name ? (
                  <div style={{
                    width: 24, height: 24, borderRadius: 4, flexShrink: 0,
                    background: `${brandColor}22`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, color: brandColor,
                  }}>
                    {branding.company_name.charAt(0).toUpperCase()}
                  </div>
                ) : null}
                <span style={{ fontSize: 13, fontWeight: 600, color: tokens.color.text.secondary, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {branding.company_name || ""}
                </span>
              </div>
            </header>

            {/* Page content */}
            <PreviewBanner />
            <DemoBanner />
            <BillingBanner />
            <main style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
              {/* Global container — full-width SaaS layout, 32px padding, no max-width cap */}
              <div style={{ padding: "24px 32px", minWidth: 0 }}>
              {!apiOnline && (
                <div style={{ background: 'var(--st-error)', color: 'var(--bg-page)', padding: '6px 12px', fontSize: 12, textAlign: 'center', borderRadius: 6, marginBottom: 16, fontFamily: 'inherit' }}>
                  Connection lost — retrying...
                </div>
              )}
              {children}
              </div>
            </main>
          </div>
        </>
      )}
      <HelpPanel
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
      />
      <ChatFABPanel unreadCount={msgUnread} onMessagesRead={fetchMsgUnread} />
      {showTour && <OnboardingTour onComplete={handleTourComplete} onDismiss={handleTourDismiss} userName={tourUser?.name} userRole={tourUser?.role} companyName={tourUser?.companyName} />}
      <WhatsNewModal />
    </div>
  )
}
